import { LightThemeTable } from "components/LightThemeTable";
import React, { useCallback, useState, useMemo, useEffect } from "react";
import { connect, ConnectedProps, useSelector } from "react-redux";
import { State } from "state";
import { getRoles } from "state/chain_user_role";
import Constants from "helpers/constants";
import { createRequest } from "state/request";
import Loader from "components/Loader";

interface UserStore {
  retail_store_num: any;
}

interface UserData {
  email: string;
  firstName?: string;
  lastName?: string;
  stores?: UserStore[];
  role?: string;
  selected: boolean;
}

interface LocalUsersNotFoundModalProps {
  isOpen: boolean;
  users: string[];  // Array of emails not found in database
  duplicateEmails?: string[];
  chainName: string;
  chainId: string | number;
  projectName: string;
  projectId: string | number | any;
  stores: string[];  // All retail store numbers from CSV
  uploadedFile?: File | null;
  allUserEmails?: string[];  // All emails from CSV
  onNo: () => void;
  onClose: () => void;
  onSubmit?: () => void;
}

type CombinedProps = LocalUsersNotFoundModalProps & PropsFromRedux;

// Extracted styles as constants
const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "1300px",
    width: "95%",
    maxHeight: "90vh",
    overflowY: "auto" as const,
    position: "relative" as const,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  },
  closeButton: {
    position: "absolute" as const,
    top: "24px",
    right: "24px",
    background: "none",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
    color: "#000",
    lineHeight: 1,
    padding: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "10px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#000",
    margin: 0,
  },
  icon: {
    width: "40px",
    height: "40px",
    backgroundColor: "#F5A623",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  message: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "10px",
    lineHeight: 1.5,
  },
  infoBox: {
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "14px 18px",
    fontSize: "12px",
    color: "#666",
    marginBottom: "10px",
  },
  infoPill: {
    backgroundColor: "#E8F0FB",
    border: "1px solid #0755AA",
    borderRadius: "10px",
    padding: "10px 16px",
    fontSize: "12px",
    color: "#0755AA",
    fontWeight: 500,
    marginBottom: "10px",
    textAlign: "left" as const,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "24px",
  },
  tableContainer: {
    marginBottom: "10px",
  },
  storesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: "8px",
    maxHeight: "200px",
    overflowY: "auto" as const,
    padding: "4px",
  },
  storesGridScrollable: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: "8px",
    maxHeight: "200px",
    overflowY: "auto" as const,
    padding: "4px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#fafafa",
  },
  storeBox: {
    backgroundColor: "#f9f9f9",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "#555",
  },
  checkbox: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
    accentColor: "#F5A623",
  },
  errorMessage: {
    fontSize: "12px",
    color: "#EF4444",
    margin: "4px 0 0 0",
  },
  inputField: {
    padding: "6px 10px",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#374151",
    backgroundColor: "white",
    width: "100%",
    fontFamily: "inherit",
  },
} as const;

// Icon component extracted 
const WarningIcon: React.FC = () => (
  <div style={styles.icon}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2L2 7L12 12L22 7L12 2Z"
        fill="white"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 17L12 22L22 17"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 12L12 17L22 12"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

// Only allows letters, numbers, spaces, hyphens, and apostrophes (valid name characters)
const NAME_REGEX = /^[a-zA-Z0-9\s\-']*$/;

const UsersNotFoundInDatabaseModal: React.FC<CombinedProps> = ({
  isOpen,
  users,
  chainName,
  chainId,
  projectName,
  projectId,
  stores,
  uploadedFile,
  allUserEmails,
  onNo,
  onClose,
  getRoles,
  roles, // coming from Redux
  createRequest, // coming from Redux
  duplicateEmails,
  onSubmit
}) => {

  const [showTargetUsers, setShowTargetUsers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<UserData[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: {
      role?: boolean;
      firstName?: boolean;
      lastName?: boolean;
    }
  }>({});
  const authUser = useSelector((state: State) => state.auth_user.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = authUser?.id;
  const userEmail = authUser?.email;

  const normalizeEmail = (email: string) => String(email || "").trim().toLowerCase();
  const getUniqueEmails = (emails: string[]) => {
    const seen = new Set<string>();
    return emails.reduce<string[]>((acc, email) => {
      const normalized = normalizeEmail(email);
      if (!normalized || seen.has(normalized)) {
        return acc;
      }
      seen.add(normalized);
      acc.push(String(email || "").trim());
      return acc;
    }, []);
  };

  // Reset showTargetUsers when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowTargetUsers(false);
    }
  }, [isOpen]);

  // Style tag for checkbox color and input placeholders
  useEffect(() => {
    const styleId = 'custom-checkbox-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        input[type="checkbox"] {
          accent-color: #F5A623;
        }
        input::placeholder {
          font-family: inherit;
          opacity: 0.6;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Fetch roles on component mount
  useEffect(() => {
    getRoles();
  }, [getRoles]);

  // Parse CSV and populate table data on mount so stores are ready for confirmation table
  useEffect(() => {
    const parseAndSetData = async () => {
      const uniqueEmails = getUniqueEmails(users);
      if (!uploadedFile || uniqueEmails.length === 0) {
        // Initialize empty table data
        // Auto-select if only one user
        const shouldAutoSelect = uniqueEmails.length === 1;
        const emptyData: UserData[] = uniqueEmails.map((email) => ({
          email,
          firstName: "",
          lastName: "",
          stores: [],
          role: "",
          selected: shouldAutoSelect,
        }));
        setTableData(emptyData);
        setSelectAll(shouldAutoSelect);
        return;
      }

      setIsLoading(true);

      try {
        const reader = new FileReader();

        reader.onload = (e: any) => {
          try {
            const raw = e.target.result as ArrayBuffer;
            const bytes = new Uint8Array(raw);
            let enc = "utf-8";
            if (bytes[0] === 0xff && bytes[1] === 0xfe) enc = "utf-16le";
            else if (bytes[0] === 0xfe && bytes[1] === 0xff) enc = "utf-16be";
            else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
              let valid = true;
              for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
                if (bytes[i] > 0x7f && bytes[i] < 0xa0) { valid = false; break; }
              }
              if (!valid) enc = "windows-1252";
            }
            const text = new TextDecoder(enc).decode(raw);
            const lines = text.split("\n").filter((line) => line.trim() !== "");
            const emailToStoresMap = new Map<string, string[]>();

            if (lines.length > 0) {
              const headers = lines[0].split(",").map((col: string) => col.trim());
              const emailIndex = headers.findIndex((header) => header === "Email Address");
              const storeIndex = headers.findIndex((header) => header === "Retail Store Number");

              if (emailIndex !== -1 && storeIndex !== -1) {
                for (let i = 1; i < lines.length; i++) {
                  const row = lines[i].split(",").map((cell) => cell.trim());
                  const email = row[emailIndex];
                  const store = row[storeIndex];

                  if (email && store) {
                    const normalized = normalizeEmail(email);
                    if (!emailToStoresMap.has(normalized)) {
                      emailToStoresMap.set(normalized, []);
                    }
                    if (!emailToStoresMap.get(normalized)!.includes(store)) {
                      emailToStoresMap.get(normalized)!.push(store);
                    }
                  }
                }
              }
            }

            // Map users to their stores and Auto-select if only one user
            const shouldAutoSelect = uniqueEmails.length >= 1;
            const data: UserData[] = uniqueEmails.map((email) => {
              const userStores = emailToStoresMap.get(normalizeEmail(email)) || [];
              return {
                email,
                firstName: "",
                lastName: "",
                stores: userStores.map((storeNum) => ({
                  retail_store_num: storeNum,
                })),
                role: "",
                selected: shouldAutoSelect,
              };
            });

            setTableData(data);
            setSelectAll(shouldAutoSelect);
            setIsLoading(false);
          } catch (error) {
            console.error("Error parsing CSV:", error);
            setIsLoading(false);
          }
        };

        reader.onerror = () => {
          console.error("Error reading file");
          setIsLoading(false);
        };

        reader.readAsArrayBuffer(uploadedFile);
      } catch (error) {
        console.error("Error in parseAndSetData:", error);
        setIsLoading(false);
      }
    };

    parseAndSetData();
  }, [users, uploadedFile]);

  const handleSubmit = useCallback(async () => {
    // Check if any selected user is missing required fields
    const errors: {
      [key: number]: {
        role?: boolean;
        firstName?: boolean;
        lastName?: boolean;
      }
    } = {};
    let hasErrors = false;

    tableData.forEach((user, index) => {
      if (user.selected) {
        const userErrors: { role?: boolean; firstName?: boolean; lastName?: boolean } = {};

        if (!user.role || user.role.trim() === "") {
          userErrors.role = true;
          hasErrors = true;
        }

        if (!user.firstName || user.firstName.trim() === "") {
          userErrors.firstName = true;
          hasErrors = true;
        }

        if (!user.lastName || user.lastName.trim() === "") {
          userErrors.lastName = true;
          hasErrors = true;
        }

        if (Object.keys(userErrors).length > 0) {
          errors[index] = userErrors;
        }
      }
    });

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }


    // Get only selected users with valid data and dedupe by email
    const selectedUsers: any[] = [];
    const seenEmails = new Set<string>();
    tableData.forEach((user) => {
      if (
        user.selected &&
        user.role &&
        user.role.trim() !== "" &&
        user.firstName &&
        user.firstName.trim() !== "" &&
        user.lastName &&
        user.lastName.trim() !== ""
      ) {
        const normalized = normalizeEmail(user.email);
        if (!normalized || seenEmails.has(normalized)) {
          return;
        }
        seenEmails.add(normalized);
        selectedUsers.push(user);
      }
    });

    // Find the role_id for each user based on their selected role
    const getUserRoleId = (roleName: string) => {
      const roleObj = roles?.find((role: any) => role.role === roleName);
      return roleObj?.id || null;
    };

    // Prepare API payload in the correct format
    const apiPayload = {
      requestType: Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_PROJECT_ADMIN,
      context: {
        initiator_user_id: userId,
        initiator_email: userEmail,
        request_intiated_from: "CREATE PROJECT USER STORE MAPPING",
        payload_from: "CREATE PROJECT USER STORE MAPPING"
      },
      data: {
        USER_ONBOARDING_AND_CHAIN_ACCESS_BY_PROJECT_ADMIN: selectedUsers.map(user => ({
          chain_id: Number(chainId),
          user_email: user.email,
          user_role_id: getUserRoleId(user.role!),
          user_role: user.role!,
          first_name: user.firstName,
          last_name: user.lastName,
          stores: user.stores?.map((store: UserStore) => store.retail_store_num) || []
        }))
      }
    };

    setIsSubmitting(true);
    try {
      await createRequest(apiPayload);
      // onClose();
      onSubmit?.();
    } catch (error) {
      console.error("Error creating request approval:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [projectName, projectId, chainName, chainId, stores, users, uploadedFile, allUserEmails, tableData, roles, onClose, userId, userEmail, createRequest]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setTableData(tableData.map((user) => ({ ...user, selected: newSelectAll })));
  };

  const handleUserSelect = (index: number) => {
    const newTableData = [...tableData];
    newTableData[index].selected = !newTableData[index].selected;
    setTableData(newTableData);
    setSelectAll(newTableData.every((user) => user.selected));

    // Clear validation errors if unchecking
    if (!newTableData[index].selected) {
      const newErrors = { ...validationErrors };
      delete newErrors[index];
      setValidationErrors(newErrors);
    }
  };

  const handleFirstNameChange = (index: number, value: string) => {
    // Reject input if it contains special characters or numbers
    if (!NAME_REGEX.test(value)) return;

    const newTableData = [...tableData];
    newTableData[index].firstName = value;
    setTableData(newTableData);

    // Clear validation error if field is filled
    if (value.trim() !== "") {
      const newErrors = { ...validationErrors };
      if (newErrors[index]) {
        delete newErrors[index].firstName;
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      setValidationErrors(newErrors);
    }
  };

  const handleLastNameChange = (index: number, value: string) => {
    // Reject input if it contains special characters or numbers
    if (!NAME_REGEX.test(value)) return;

    const newTableData = [...tableData];
    newTableData[index].lastName = value;
    setTableData(newTableData);

    // Clear validation error if field is filled
    if (value.trim() !== "") {
      const newErrors = { ...validationErrors };
      if (newErrors[index]) {
        delete newErrors[index].lastName;
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      setValidationErrors(newErrors);
    }
  };

  // Check if any user is selected
  const hasSelectedUsers = tableData.some(user => user.selected);

  const handlePageChange = (page: number, pageSize: number) => {

  };

  // Cell style
  const cellStyle: React.CSSProperties = {
    fontSize: "12px",
    color: "#374151",
  };

  // Define columns for LightThemeTable
  const columns = [
    {
      field: "checkbox",
      header: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAll}
          style={styles.checkbox}
        />
      ),
      formatter: (_: any, row: UserData, index: number) => (
        <input
          type="checkbox"
          checked={row.selected}
          onChange={() => handleUserSelect(index)}
          style={styles.checkbox}
        />
      ),
    },
    {
      field: "email",
      header: "Email",
      formatter: (_: any, row: UserData) => (
        <div style={{ ...cellStyle, fontWeight: 500 }}>
          {row.email}
        </div>
      ),
    },
    {
      field: "firstName",
      header: "First Name",
      formatter: (_: any, row: UserData, index: number) => (
        <div style={{ width: '100%' }}>
          <input
            type="text"
            style={{
              ...styles.inputField,
              border: validationErrors[index]?.firstName ? "2px solid #EF4444" : "1px solid #D1D5DB",
            }}
            value={row.firstName || ""}
            onChange={(e) => handleFirstNameChange(index, e.target.value)}
            placeholder="Enter first name"
          />
          {validationErrors[index]?.firstName && row.selected && (
            <p style={styles.errorMessage}>
              First name is required
            </p>
          )}
        </div>
      ),
    },
    {
      field: "lastName",
      header: "Last Name",
      formatter: (_: any, row: UserData, index: number) => (
        <div style={{ width: '100%' }}>
          <input
            type="text"
            style={{
              ...styles.inputField,
              border: validationErrors[index]?.lastName ? "2px solid #EF4444" : "1px solid #D1D5DB",
            }}
            value={row.lastName || ""}
            onChange={(e) => handleLastNameChange(index, e.target.value)}
            placeholder="Enter last name"
          />
          {validationErrors[index]?.lastName && row.selected && (
            <p style={styles.errorMessage}>
              Last name is required
            </p>
          )}
        </div>
      ),
    },
    {
      field: "stores",
      header: "Associated Stores",
      formatter: (_: any, row: UserData) => {
        if (!row.stores || row.stores.length === 0) {
          return <div style={{ ...cellStyle, color: "#9CA3AF" }}>No stores found</div>;
        }

        // Show stores as comma-separated text
        const storesArr = Array.isArray(row.stores) ? row.stores : [];
        const storeNumbers = storesArr.map(store => store.retail_store_num).join(", ");

        return (
          <div style={cellStyle}>
            {storeNumbers}
          </div>
        );
      },
    },
    {
      field: "role",
      header: "Role",
      formatter: (_: any, row: UserData, index: number) => (
        <div style={{ width: '100%' }}>
          <select
            style={{
              padding: "6px 10px",
              border: validationErrors[index]?.role ? "2px solid #EF4444" : "1px solid #D1D5DB",
              borderRadius: "6px",
              fontSize: "12px",
              color: row.role ? "#374151" : "#9CA3AF",
              cursor: "pointer",
              backgroundColor: "white",
              minWidth: "150px",
              width: "100%",
            }}
            value={row.role || ""}
            onChange={(e) => {
              // Update the role in tableData
              const updatedData = [...tableData];
              updatedData[index].role = e.target.value;
              setTableData(updatedData);

              // Clear validation error if role is selected
              if (e.target.value && e.target.value.trim() !== "") {
                const newErrors = { ...validationErrors };
                if (newErrors[index]) {
                  delete newErrors[index].role;
                  if (Object.keys(newErrors[index]).length === 0) {
                    delete newErrors[index];
                  }
                }
                setValidationErrors(newErrors);
              }
            }}
          >
            <option value="" disabled>Select Role</option>
            {roles?.filter((role: any) => role.role !== Constants.AB_SYSTEM_ADMIN)?.map((role: any, idx: any) => (
              <option key={role.id} value={role.role}>
                {role.role}
              </option>
            ))}
          </select>
          {validationErrors[index]?.role && row.selected && (
            <p style={styles.errorMessage}>
              Please assign a role to this user
            </p>
          )}
        </div>
      ),
    },
  ];

  // Early return for better performance
  if (!isOpen) return null;

  const hasManyStores = stores && stores.length > 12;
  const storesGridStyle = hasManyStores ? styles.storesGridScrollable : styles.storesGrid;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button
          style={styles.closeButton}
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>

        <div style={styles.header}>
          <WarningIcon />
          <h2>User(s) Not Found in Database</h2>
        </div>
        {duplicateEmails && duplicateEmails.length > 0 && (
          <p style={{ ...styles.message, color: "#8A5A00" }}>
          <strong>Note:</strong> A few user email IDs were repeated in the uploaded CSV. Below are the unique user IDs that are not in the database.
          </p>
        )}

        <p style={styles.message}>
          These users can't be added to the project because they don't have Reset IQ accounts yet.{showTargetUsers && " Select users to send an onboarding request to System Admin."}
        </p>
        <hr style={{ border: 'none', borderTop: '1px solid #F0F2F5', margin: '15px 0' }} />
        <p style={styles.message}>{users.length} User(s) Not Found</p>

        <div
          style={{
            ...styles.infoPill,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "2px solid #0755AA",
              color: "#0755AA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: "bold",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            !
          </span>

          <span>
            Once the users are onboarded, you can re-upload the mapping file
          </span>
        </div>

        {/* Confirmation prompt — shown before Target Users section */}
        {!showTargetUsers ? (
          <>
            {/* Users-not-found table */}
            <div style={styles.tableContainer}>
              <LightThemeTable
                data={tableData.length > 0 ? tableData : users.map((email) => ({ email, stores: [], selected: false }))}
                columns={[
                  {
                    field: "email",
                    header: "Email",
                    formatter: (_: any, row: UserData) => (
                      <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                        {row.email}
                      </div>
                    ),
                  },
                  {
                    field: "stores",
                    header: "Associated Stores",
                    formatter: (_: any, row: UserData) => {
                      if (!row.stores || row.stores.length === 0) {
                        return <div style={{ fontSize: "12px", color: "#9CA3AF" }}>No stores found</div>;
                      }
                      const storesArr2 = Array.isArray(row.stores) ? row.stores : [];
                      const storeNumbers = storesArr2.map(store => store.retail_store_num).join(", ");
                      return <div style={{ fontSize: "12px", color: "#374151" }}>{storeNumbers}</div>;
                    },
                  },
                ]}
                onPageChange={handlePageChange}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                isLoading={isLoading}
              />
            </div>

            <p style={{ ...styles.message, fontSize: "14px", color: "#374151", marginTop: "8px" }}>
              Do you want to submit an onboarding request for the user(s) not found?
            </p>
            <div style={styles.buttonsContainer}>
              <button className="primary-button" onClick={onNo}>
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={() => setShowTargetUsers(true)}
              >
                Yes
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.tableContainer}>
              <h2 style={{ margin: "0 0 12px 0" }}>
                Target Users
              </h2>
              <LightThemeTable
                data={tableData}
                columns={columns}
                onPageChange={handlePageChange}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                isLoading={isLoading}
              />
            </div>

            <div style={{ ...styles.buttonsContainer, alignItems: 'center' }}>
              <button className="primary-button" onClick={onNo}>
                Cancel
              </button>
              {isSubmitting && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Loader height={20} width={20} custom={true} />
                </div>
              )}
              <button
                className="primary-button"
                onClick={handleSubmit}
                disabled={!hasSelectedUsers || isSubmitting}
                style={{
                  opacity: !hasSelectedUsers || isSubmitting ? 0.5 : 1,
                  cursor: !hasSelectedUsers || isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const connecter = connect(
  (state: State) => ({
    roles: state.chains_users_role.roles,
  }),
  {
    getRoles,
    createRequest,
  }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(UsersNotFoundInDatabaseModal);